/**
 * Property Maintenance → Import (ALN). Parity with Angular `/import-data`.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, X } from 'lucide-react'
import { IntegratedFileSelect } from '@/components/ui/IntegratedFileSelect'
import { useUserRole } from '@/hooks/useUserRole'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import {
  AdvancedDateRangePicker,
  getAngularSystemAnnouncementPresets,
} from '@/components/ui/AdvancedDateRangePicker'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { showApiError, showError, showSuccess } from '@/lib/utils/notifications'
import { getUserManagementEditUrl } from '@/lib/utils/userManagementNavigation'
import { getTeams } from '../services/teamsService'
import {
  getAlnHistory,
  getAlnSync,
  postAddDraft,
  postImportChanges,
  postUpdateDraft,
} from '../services/importDataService'
import type { AlnRow, ImportDataType, ImportFilterState } from '../types/importData'
import { getAlnSyncColumns, getAlnHistoryColumns, getAlnUploadColumns } from '../utils/alnSyncTableColumns'
import { renderAlnSyncCell } from '../utils/alnSyncTableCells'
import type { TeamRecord } from '../types/team'
import { cn } from '@lib/utils/helpers'
import parityStyles from '@/features/invitee/styles/invitesParity.module.css'
import {
  ListingDataTableCard,
  LISTING_DATA_TABLE_BODY_CLASS,
  LISTING_DATA_TABLE_CLASS,
  LISTING_DATA_TABLE_HEAD_CLASS,
} from '@/components/ui/listingDataTable'
import {
  entityEditTabButtonClassName,
} from '@/components/ui/entityEditTabNavStyles'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { createDefaultImportFilterState } from '../utils/importDataDateDefaults'
import { AlnImportViewDetailModal } from '../components/AlnImportViewDetailModal'
import { AlnImportHistoryMailModal } from '../components/AlnImportHistoryMailModal'

/** Same values as Angular `formControlName="listed"` (only one active; API still gets a single `listed` param). */
const LISTED_CHOICES: { v: string; l: string }[] = [
  { v: '', l: 'All' },
  { v: '1', l: 'Active' },
  { v: '0', l: 'Not Active' },
]

const IMPORT_TYPE_OPTIONS: { value: ImportDataType; label: string }[] = [
  { value: '', label: 'Upload file type' },
  { value: 'property_name', label: 'Property changes' },
  { value: 'management_company', label: 'Company changes' },
  { value: 'manager_change', label: 'Manager change' },
  { value: 'area_supervisor', label: 'Area supervisor' },
  { value: 'pmc_import', label: 'Full PMC import' },
  { value: 'property_import', label: 'Full property import' },
  { value: 'occupancy', label: 'Occupancy' },
]

import type {
  ImportPageApplyKind,
  ImportPageDataTableColumn,
  ImportPageDupState,
  ImportPageTabId,
} from '../types'

const IMPORT_DATA_TABS: { id: ImportPageTabId; label: string; testId: string }[] = [
  { id: 'alnupload', label: 'ALN Upload', testId: 'import-data-tab-alnupload' },
  { id: 'alnsync', label: 'ALN Sync', testId: 'import-data-tab-alnsync' },
  { id: 'history', label: 'History', testId: 'import-data-tab-history' },
]

function toRows(data: unknown): AlnRow[] {
  if (Array.isArray(data)) return data as AlnRow[]
  if (data && typeof data === 'object' && 'data' in (data as object) && Array.isArray((data as { data: unknown }).data)) {
    return (data as { data: AlnRow[] }).data
  }
  return []
}

function isLinkKey(key: string, row: AlnRow): { href: string; external?: boolean } | null {
  const v = row[key]
  if (v == null || v === '') return null
  const s = String(v)
  if (s === '0') return null
  if (key === 'property_id' || key === 'fk_property_id') {
    return { href: `/property/edit/${s}` }
  }
  if (['previous_company_id', 'current_company_id', 'fk_company_id'].includes(key)) {
    return { href: `/company/edit/${s}` }
  }
  if (key === 'previous_manager_user_id' || (key.includes('user_id') && key.includes('manager'))) {
    return { href: getUserManagementEditUrl(s), external: true }
  }
  return null
}

function CellValue({ k, row }: { k: string; row: AlnRow }) {
  const v = row[k]
  const link = isLinkKey(k, row)
  const text = v == null || v === '' ? '—' : String(v)
  if (link) {
    if (link.external) {
      return (
        <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-button underline">
          {text}
        </a>
      )
    }
    return (
      <Link to={link.href} className="text-button underline" target="_blank" rel="noreferrer">
        {text}
      </Link>
    )
  }

  // Handle common date fields
  if (k.endsWith('_at') || k.includes('date')) {
    if (v != null && v !== '') {
      let d = new Date(v as string | number)
      if (typeof v === 'number' && v > 0 && v < 10000000000) {
        d = new Date(v * 1000)
      }
      if (!Number.isNaN(d.getTime())) {
        const formatted = `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`
        return <span className="text-slate-700">{formatted}</span>
      }
    }
  }

  return <span className="text-slate-700">{text}</span>
}

function DataTable({
  rows,
  /** When set, fixes column order and headers (Angular parity). Otherwise derived from the first row’s keys. */
  columns,
  renderCell,
  selectable,
  selected = [],
  onToggle,
  onToggleAll,
  allSelected = false,
  onView,
  fillHeight = false,
}: {
  rows: AlnRow[]
  columns?: ImportPageDataTableColumn[]
  renderCell?: (columnKey: string, row: AlnRow) => ReactNode
  selectable?: boolean
  selected?: (number | string)[]
  onToggle?: (id: number | string, checked: boolean) => void
  onToggleAll?: (checked: boolean) => void
  allSelected?: boolean
  onView?: (row: AlnRow) => void
  /** Expand to use remaining layout height instead of a fixed max height. */
  fillHeight?: boolean
}) {
  // Hooks must run before any early return — keep useMemo at the top and guard
  // the empty-rows case inside the factory so rows[0] access stays safe.
  const colDefs: ImportPageDataTableColumn[] = useMemo(() => {
    if (columns?.length) return columns
    const firstRow = rows[0]
    if (!firstRow) return []
    const keys = Object.keys(firstRow as object).filter((k) => k !== '__proto__')
    return keys.map((key) => ({ key, label: key.replace(/_/g, ' ') }))
  }, [columns, rows])

  if (!rows.length) {
    return (
      <p
        className={cn(
          'rounded-lg border border-slate-200 bg-slate-50/50 text-center text-sm text-slate-500',
          fillHeight ? 'flex min-h-0 flex-1 items-center justify-center py-4' : 'py-6',
        )}
      >
        No rows to display.
      </p>
    )
  }

  if (!colDefs.length) {
    return <p className="text-slate-500">No columns.</p>
  }

  const showTrailingView = Boolean(onView && !renderCell)

  return (
    <ListingDataTableCard className={cn(fillHeight && 'flex min-h-0 flex-1 flex-col')}>
      <div
        className={cn(
          'min-h-0 min-w-0 overflow-auto',
          fillHeight ? 'flex-1' : 'max-h-[min(60vh,520px)]',
        )}
      >
      <table className={cn(parityStyles.propertyListingTable, parityStyles.listingTable, LISTING_DATA_TABLE_CLASS, '!table-auto divide-y divide-slate-200 text-left')}>
        <thead
          className={cn(
            LISTING_DATA_TABLE_HEAD_CLASS,
            'sticky top-0 z-[1] text-xs font-semibold uppercase',
          )}
        >
          <tr>
            {selectable && (
              <th className="whitespace-nowrap px-3 py-2 !text-left">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => onToggleAll?.(e.target.checked)}
                    className="shrink-0 rounded border-slate-400 focus:outline-none focus:ring-2 focus:ring-button/30"
                  />
                  Select All
                </label>
              </th>
            )}
            {colDefs.map((c) => (
              <th key={c.key} className="whitespace-nowrap px-3 py-2 !text-left">
                {c.label}
              </th>
            ))}
            {showTrailingView && <th className="px-3 py-2 !text-left">View</th>}
          </tr>
        </thead>
        <tbody className={cn(LISTING_DATA_TABLE_BODY_CLASS, 'bg-white')}>
          {rows.map((row, i) => {
            const id = row.id
            return (
              <tr key={String(id ?? i)} className={parityStyles.listingTableRow}>
                {selectable && (
                  <td className="px-3 py-2">
                    {id != null ? (
                      <input
                        type="checkbox"
                        checked={selected.includes(id)}
                        onChange={(e) => onToggle?.(id, e.target.checked)}
                        className="shrink-0 rounded border-slate-400 focus:outline-none focus:ring-2 focus:ring-button/30"
                      />
                    ) : null}
                  </td>
                )}
                {colDefs.map((c) => (
                  <td
                    key={c.key}
                    className={cn('max-w-xs px-3 py-2 align-top', !renderCell && 'truncate')}
                  >
                    {renderCell ? renderCell(c.key, row) : <CellValue k={c.key} row={row} />}
                  </td>
                ))}
                {showTrailingView && onView && (
                  <td className="px-3 py-2">
                    <button type="button" className={cn(secondaryEntityActionButtonClass, "px-3 py-1 text-xs")} onClick={() => onView(row)}>
                      View
                    </button>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
      </div>
    </ListingDataTableCard>
  )
}

function pickDuplicateList(res: { data?: { aln_import_id?: string | number }[] }): (string | number)[] {
  const d = res.data
  if (!Array.isArray(d) || !d.length) return []
  return d.map((x) => x.aln_import_id).filter((x) => x != null) as (string | number)[]
}

export function ImportPage() {
  const { hasPolicy } = useUserRole()
  const canApply = hasPolicy(['import_apply_changes'])
  const canUndo = hasPolicy(['import_undo_changes'])
  const canImportExport = hasPolicy(['import_export_option'])
  const canImportEmail = hasPolicy(['import_email_option'])

  const [tab, setTab] = useState<ImportPageTabId>('alnupload')
  const [filter, setFilter] = useState<ImportFilterState>(() => createDefaultImportFilterState())
  const [teams, setTeams] = useState<TeamRecord[]>([])
  const [file, setFile] = useState<File | null>(null)
  const [fileName, setFileName] = useState('')
  const [processing, setProcessing] = useState(false)
  const [uploadResults, setUploadResults] = useState<AlnRow[]>([])
  const [syncRows, setSyncRows] = useState<AlnRow[]>([])
  const [historyRows, setHistoryRows] = useState<AlnRow[]>([])
  const [selectedSync, setSelectedSync] = useState<(number | string)[]>([])
  const [selectedHistory, setSelectedHistory] = useState<(number | string)[]>([])
  const [detail, setDetail] = useState<AlnRow | null>(null)
  const [dupState, setImportPageDupState] = useState<ImportPageDupState>({ open: false })
  const [actionConfirm, setActionConfirm] = useState<{ open: true; action: ImportPageApplyKind } | { open: false }>({ open: false })
  const [historyMailModal, setHistoryMailModal] = useState<'email' | 'export' | null>(null)

  /** Angular `import-data.component.ts` `setDateRanges()` — six ngx presets + three calendar years. */
  const importDateRangePresets = useMemo(() => getAngularSystemAnnouncementPresets(), [])

  const loadLists = useCallback(async () => {
    if (!filter.type) {
      setSyncRows([])
      setHistoryRows([])
      return
    }
    setProcessing(true)
    try {
      const [s, h] = await Promise.all([getAlnSync(filter), getAlnHistory(filter)])
      setSyncRows(s)
      setHistoryRows(h)
    } catch (e) {
      showApiError(e)
    } finally {
      setProcessing(false)
    }
  }, [filter])

  useEffect(() => {
    getTeams()
      .then(setTeams)
      .catch(() => setTeams([]))
  }, [])

  useEffect(() => {
    if (filter.type) void loadLists()
  }, [filter.type, filter.listed, filter.pmc_import_type, filter.fk_team_id, filter.dateStart, filter.dateEnd, loadLists])

  const patch = (p: Partial<ImportFilterState>) => setFilter((f) => ({ ...f, ...p }))

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) {
      setFile(null)
      setFileName('')
      return
    }
    const mb = f.size / 1024 / 1024
    if (mb > 2) {
      showError('File Size Exceeds Allowable Limit of 2 MB.', false)
      e.target.value = ''
      setFile(null)
      setFileName('')
      return
    }
    setFile(f)
    setFileName(f.name)
  }

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    if (tab !== 'alnupload') {
      return
    }
    if (!filter.type) {
      showError('Select an upload file type.', false)
      return
    }
    if (tab === 'alnupload' && !file) {
      showError('Select a file to upload.', false)
      return
    }
    setProcessing(true)
    try {
      const raw = await postImportChanges(file, filter)
      setUploadResults(toRows(raw))
      showSuccess('File uploaded Successfully')
    } catch (err) {
      showApiError(err)
    } finally {
      setProcessing(false)
    }
  }

  /** Angular `resetFilter()` only clears file + upload type + preview — not team, listed, or date range. */
  const resetForm = () => {
    setFile(null)
    setFileName('')
    setUploadResults([])
    setFilter((f) => ({ ...f, type: '' }))
  }

  const runDuplicateDialog = (res: { data?: { aln_name?: string; liberty_name?: string; aln_import_id?: string | number }[]; message?: string }, t: ImportPageTabId, action: ImportPageApplyKind) => {
    const dups = res.data
    if (!Array.isArray(dups) || !dups.length) return false
    const message =
      (filter.type === 'pmc_import' ? 'The following companies may share the same address:\n\n' : 'Review duplicates:\n\n') +
      dups.map((x) => `${x.aln_name ?? '—'} => ${x.liberty_name ?? '—'}`).join('\n') +
      '\n\nProceed with these IDs?'
    const ids = pickDuplicateList({ data: dups })
    setImportPageDupState({ open: true, message, ids, tab: t, action })
    return true
  }

  const requestApply = (action: ImportPageApplyKind) => {
    if (!filter.type) {
      showError('Select a file type first.', false)
      return
    }
    if (tab === 'alnsync' && (action === 'apply' || action === 'skip') && !selectedSync.length) {
      showError('Select at least one row.', false)
      return
    }
    if (tab === 'history' && action === 'undo' && !selectedHistory.length) {
      showError('Select at least one row.', false)
      return
    }
    if (tab === 'alnupload' && action === 'apply' && !uploadResults.length) {
      showError('Upload and preview data first.', false)
      return
    }
    setActionConfirm({ open: true, action })
  }

  const executeApply = async (action: ImportPageApplyKind) => {
    setProcessing(true)
    try {
      let res: { data?: unknown[]; message?: string } = {}

      if (tab === 'alnupload' && action === 'apply') {
        res = await postAddDraft(filter, file, {})
      } else if (tab === 'alnsync') {
        res = await postUpdateDraft(filter, { action_type: action === 'skip' ? 'skip' : 'apply', selected_id: selectedSync })
      } else {
        res = await postUpdateDraft(filter, { action_type: 'undo', selected_id: selectedHistory })
      }

      if (runDuplicateDialog(res as { data: { aln_name?: string; liberty_name?: string; aln_import_id?: string | number }[] }, tab, action)) {
        return
      }

      const msg = (res as { message?: string }).message
      if (msg && (msg.includes('Legal Name') || msg === 'The Companys Legal Name needs to be verified')) {
        showError('Changes Applied Successfully but The Companys Legal Name needs to be verified', true)
      } else {
        showSuccess('Changes Applied Successfully')
      }
      setSelectedSync([])
      setSelectedHistory([])
      await loadLists()
    } catch (e) {
      showApiError(e)
    } finally {
      setProcessing(false)
    }
  }

  const confirmDuplicate = async () => {
    if (!dupState.open) return
    setProcessing(true)
    try {
      const { tab: dTab, action, ids } = dupState
      if (dTab === 'alnupload') {
        await postAddDraft(filter, file, { proceed: true, selected_id: ids })
      } else {
        const action_type: 'apply' | 'skip' | 'undo' =
          dTab === 'history' ? 'undo' : action === 'skip' ? 'skip' : 'apply'
        await postUpdateDraft(filter, { action_type, selected_id: ids, proceed: true })
      }
      showSuccess('Changes Applied Successfully')
      setImportPageDupState({ open: false })
      setSelectedSync([])
      setSelectedHistory([])
      await loadLists()
    } catch (e) {
      showApiError(e)
    } finally {
      setProcessing(false)
    }
  }

  const allSyncSelected = useMemo(
    () => syncRows.length > 0 && selectedSync.length === syncRows.filter((r) => r.id != null).length,
    [syncRows, selectedSync],
  )
  const allHistorySelected = useMemo(
    () => historyRows.length > 0 && selectedHistory.length === historyRows.filter((r) => r.id != null).length,
    [historyRows, selectedHistory],
  )

  const toggleRow = (list: 'sync' | 'history', id: number | string, checked: boolean) => {
    if (list === 'sync') {
      setSelectedSync((p) => (checked ? [...p, id] : p.filter((x) => x !== id)))
    } else {
      setSelectedHistory((p) => (checked ? [...p, id] : p.filter((x) => x !== id)))
    }
  }

  const mainNeedsGrow =
    tab === 'alnsync' || tab === 'history' || (tab === 'alnupload' && uploadResults.length > 0)

  /** Reserve a fixed-width action slot whenever user can see any import action, so tabs never resize when switching tabs. */
  const reserveActionColumn = canApply || canUndo

  return (
    <div
      className="flex min-h-screen flex-col bg-[#f8fafc] font-sans text-slate-900"
      data-testid="import-data-page"
    >
      <PageHeader title="IMPORT DATA" />

      <div
        className={cn(
          'relative flex w-full flex-col px-3 py-4 sm:px-6 sm:py-6',
          mainNeedsGrow ? 'min-h-0 flex-1' : 'pb-2',
        )}
      >
        {processing && (
          <div
            className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-slate-900/10"
            aria-hidden
          >
            <Loader2 className="h-10 w-10 animate-spin text-button" />
          </div>
        )}

        <div
          className={cn(
            'flex min-w-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm',
            mainNeedsGrow && 'min-h-0 flex-1',
          )}
        >
          {tab === 'history' &&
            historyRows.length > 0 &&
            (canImportExport || canImportEmail) && (
              <div
                className="flex flex-wrap items-center justify-end gap-2 border-b border-slate-200 bg-slate-50/90 px-3 py-2 sm:px-4"
                data-testid="import-history-email-export-actions"
              >
                {canImportEmail && (
                  <button
                    type="button"
                    onClick={() => setHistoryMailModal('email')}
                    className={cn(secondaryEntityActionButtonClass, "px-3 py-1.5")}
                  >
                    Email
                  </button>
                )}
                {canImportExport && (
                  <button
                    type="button"
                    onClick={() => setHistoryMailModal('export')}
                    className={cn(secondaryEntityActionButtonClass, "px-3 py-1.5")}
                  >
                    Export
                  </button>
                )}
              </div>
            )}
          {/*
            Shared “entity” tab look (see EntityEditTabBar) + reserved action column so the strip does not
            reflow when Apply / Skip / Undo appear (matches Product intent for property-maintenance / import).
          */}
          <div className="flex w-full min-w-0 flex-col border-b border-slate-200 lg:h-[3.5rem] lg:min-h-[3.5rem] lg:flex-row lg:items-stretch">
            <nav
              className={cn(
                'flex w-full min-w-0 flex-1 flex-nowrap items-stretch overflow-x-auto [scrollbar-width:thin]',
                'border-0',
              )}
              data-testid="import-data-tablist"
              role="tablist"
              aria-label="Import data sections"
            >
              {IMPORT_DATA_TABS.map((t) => {
                const selected = tab === t.id
                return (
                  <button
                    key={t.id}
                    type="button"
                    id={t.testId}
                    data-testid={t.testId}
                    role="tab"
                    tabIndex={selected ? 0 : -1}
                    aria-selected={selected}
                    aria-controls="import-data-panel"
                    onClick={() => setTab(t.id)}
                    className={cn(entityEditTabButtonClassName(selected), 'self-stretch')}
                  >
                    {t.label}
                  </button>
                )
              })}
            </nav>
            {reserveActionColumn && (
              <div
                className="flex min-h-12 w-full shrink-0 items-center justify-end gap-2 border-t border-slate-200 bg-white px-3 py-2 sm:px-4 lg:min-w-[20rem] lg:max-w-[20rem] lg:shrink-0 lg:border-t-0 lg:border-l lg:border-slate-200 lg:pl-4 lg:pr-4"
                aria-label="Import actions"
                data-testid="import-data-actions"
              >
                {canApply && tab === 'alnsync' && (
                  <button
                    type="button"
                    disabled={processing || !selectedSync.length || !filter.type}
                    onClick={() => requestApply('skip')}
                    className={cn(secondaryEntityActionButtonClass, "shrink-0")}
                  >
                    Skip
                  </button>
                )}
                {canApply && (tab === 'alnupload' || tab === 'alnsync') && (
                  <button
                    type="button"
                    disabled={
                      processing ||
                      (tab === 'alnupload' && !uploadResults.length) ||
                      (tab === 'alnsync' && !selectedSync.length) ||
                      !filter.type
                    }
                    onClick={() => requestApply('apply')}
                    className={cn(primaryEntityActionButtonClass, "shrink-0")}
                  >
                    Apply Changes
                  </button>
                )}
                {canUndo && tab === 'history' && (
                  <button
                    type="button"
                    disabled={processing || !selectedHistory.length || !filter.type}
                    onClick={() => requestApply('undo')}
                    className={cn(secondaryEntityActionButtonClass, "shrink-0")}
                  >
                    Undo
                  </button>
                )}
              </div>
            )}
          </div>

          <form
            id="import-data-panel"
            role="tabpanel"
            aria-labelledby={IMPORT_DATA_TABS.find((x) => x.id === tab)?.testId}
            onSubmit={handleUpload}
            className={cn('flex flex-col', mainNeedsGrow && 'min-h-0 flex-1')}
          >
            <div className="space-y-3 p-4 sm:p-5">
              {tab === 'alnupload' ? (
                <div className="grid w-full grid-cols-1 items-end gap-3 lg:grid-cols-12 lg:gap-4">
                  <div className="min-w-0 lg:col-span-2">
                    <label className="mb-1 block text-xs font-semibold text-slate-500 uppercase">Upload file type</label>
                    <select
                      className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50/80 px-3 text-sm"
                      value={filter.type}
                      onChange={(e) => patch({ type: e.target.value as ImportDataType })}
                    >
                      {IMPORT_TYPE_OPTIONS.map((o) => (
                        <option key={o.value || 'empty'} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-0 lg:col-span-5">
                    <label className="mb-1 block text-xs font-semibold text-slate-500 uppercase">File</label>
                    <div className="flex min-h-10 items-stretch gap-2">
                      <div className="min-w-0 flex-1">
                        <IntegratedFileSelect
                          key={file ? `${file.name}-${String(file.lastModified)}` : 'no-file'}
                          displayName={fileName}
                          accept={filter.type === 'occupancy' ? '.csv' : '.xlsx,.xls'}
                          disabled={processing}
                          emptyLabel="Choose file…"
                          onChange={onFile}
                          data-testid="import-aln-file"
                        />
                      </div>
                      {fileName ? (
                        <button
                          type="button"
                          className="flex h-10 w-10 shrink-0 items-center justify-center self-center rounded-lg border border-slate-200 bg-white text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-700"
                          onClick={() => {
                            setFile(null)
                            setFileName('')
                          }}
                          aria-label="Remove file"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                    {filter.type === 'occupancy' && (
                      <a
                        href="https://api.cornerstage.libertyrent.com/static/aln_occupancy_import.csv"
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1.5 inline-block text-xs text-button underline"
                      >
                        Occupancy import sample
                      </a>
                    )}
                  </div>
                  <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-end sm:justify-end lg:col-span-5">
                    <button
                      type="submit"
                      className={cn(primaryEntityActionButtonClass, "h-10 min-w-[5.5rem] flex-1 sm:flex-initial")}
                      disabled={processing}
                    >
                      Upload
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className={cn(secondaryEntityActionButtonClass, "h-10 min-w-[4.5rem] flex-1 sm:flex-initial")}
                      disabled={processing}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-500 uppercase">Upload file type</label>
                    <select
                      className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50/80 px-3 text-sm"
                      value={filter.type}
                      onChange={(e) => patch({ type: e.target.value as ImportDataType })}
                    >
                      {IMPORT_TYPE_OPTIONS.map((o) => (
                        <option key={o.value || 'empty'} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-500 uppercase">Date range</label>
                    <AdvancedDateRangePicker
                      startDate={filter.dateStart}
                      endDate={filter.dateEnd}
                      onChange={(start, end) => patch({ dateStart: start, dateEnd: end })}
                      placeholder="Select date range"
                      customPresets={importDateRangePresets}
                      monthsShown={2}
                      displayRangeFormat="angularMmddyyyy"
                    />
                  </div>
                  {teams.length > 0 && (
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-slate-500 uppercase">Team</label>
                      <select
                        className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50/80 px-3 text-sm"
                        value={filter.fk_team_id}
                        onChange={(e) => {
                          patch({ fk_team_id: e.target.value })
                        }}
                      >
                        <option value="">Select team</option>
                        {teams.map((tm) => (
                          <option key={String(tm.ID)} value={String(tm.ID)}>
                            {tm.team_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {(tab === 'alnsync' || tab === 'history') &&
                !['pmc_import', 'property_import'].includes(filter.type) && (
                  <div className="flex flex-wrap gap-5 text-sm text-slate-800">
                    {LISTED_CHOICES.map((r) => (
                      <label key={r.v || 'all'} className="inline-flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          className="mt-0.5 shrink-0 rounded border-slate-400 focus:outline-none focus:ring-2 focus:ring-button/30"
                          checked={filter.listed === r.v}
                          onChange={() => patch({ listed: r.v })}
                        />
                        {r.l}
                      </label>
                    ))}
                  </div>
                )}

              {filter.type &&
                (tab === 'alnsync' || tab === 'history') &&
                ['pmc_import', 'property_import'].includes(filter.type) && (
                  <div className="flex flex-wrap gap-4 text-sm">
                    {['', 'New', 'Updated', 'Missing'].map((label, i) => {
                      const v = i === 0 ? '' : label
                      return (
                        <label key={label} className="inline-flex items-center gap-2">
                          <input
                            type="radio"
                            name="pmc_import_type"
                            className="mt-0.5 shrink-0 border-slate-400 focus:outline-none focus:ring-2 focus:ring-button/30"
                            checked={filter.pmc_import_type === v}
                            onChange={() => patch({ pmc_import_type: v })}
                          />
                          {i === 0 ? 'All' : label}
                        </label>
                      )
                    })}
                  </div>
                )}

            </div>

            {mainNeedsGrow && (
              <div className="flex min-h-0 flex-1 flex-col border-t border-slate-200 p-4 sm:p-5">
                {tab === 'alnupload' && uploadResults.length > 0 && (
                  <>
                    <h2 className="mb-2 shrink-0 text-sm font-bold text-slate-800">Preview</h2>
                    <DataTable
                      fillHeight
                      columns={getAlnUploadColumns(filter.type)}
                      renderCell={(key, row) => renderAlnSyncCell(filter.type, key, row, setDetail)}
                      rows={uploadResults}
                    />
                  </>
                )}

                {tab === 'alnsync' && (
                  <DataTable
                    fillHeight
                    columns={getAlnSyncColumns(filter.type)}
                    renderCell={(key, row) => renderAlnSyncCell(filter.type, key, row, setDetail)}
                    rows={syncRows}
                    selectable
                    selected={selectedSync}
                    onToggle={(id, c) => toggleRow('sync', id, c)}
                    allSelected={allSyncSelected}
                    onToggleAll={(checked) => {
                      if (checked) {
                        setSelectedSync(syncRows.map((r) => r.id).filter((x) => x != null) as (string | number)[])
                      } else {
                        setSelectedSync([])
                      }
                    }}
                  />
                )}

                {tab === 'history' && (
                  <DataTable
                    fillHeight
                    columns={getAlnHistoryColumns(filter.type)}
                    renderCell={(key, row) => renderAlnSyncCell(filter.type, key, row, setDetail)}
                    rows={historyRows}
                    selectable
                    selected={selectedHistory}
                    onToggle={(id, c) => toggleRow('history', id, c)}
                    allSelected={allHistorySelected}
                    onToggleAll={(checked) => {
                      if (checked) {
                        setSelectedHistory(historyRows.map((r) => r.id).filter((x) => x != null) as (string | number)[])
                      } else {
                        setSelectedHistory([])
                      }
                    }}
                  />
                )}
              </div>
            )}
          </form>
        </div>

        <AlnImportViewDetailModal
          open={detail != null}
          onClose={() => setDetail(null)}
          row={detail}
          importType={filter.type}
        />

        <AlnImportHistoryMailModal
          isOpen={historyMailModal != null}
          mode={historyMailModal}
          onClose={() => setHistoryMailModal(null)}
          page="history"
          importType={filter.type}
        />

        {actionConfirm.open && (
          <ConfirmDialog
            isOpen
            title="Action"
            message="Do you want to apply changes?"
            primaryLabel="OK"
            onPrimary={() => {
              if (!actionConfirm.open) return
              const a = actionConfirm.action
              setActionConfirm({ open: false })
              void executeApply(a)
            }}
            secondaryLabel="Cancel"
            onSecondary={() => setActionConfirm({ open: false })}
            onDismiss={() => setActionConfirm({ open: false })}
            primaryFirst
            messageLayout="default"
            variant="info"
          />
        )}

        {dupState.open && (
          <ConfirmDialog
            isOpen
            title="Duplicate Addresses"
            message={dupState.message}
            primaryLabel="Proceed"
            onPrimary={confirmDuplicate}
            secondaryLabel="Cancel"
            onSecondary={() => setImportPageDupState({ open: false })}
            onDismiss={() => setImportPageDupState({ open: false })}
            primaryFirst
            messageLayout="default"
            variant="warning"
          />
        )}
      </div>
    </div>
  )
}
